import { useEffect, useState } from 'react';

/**
 * Optimistische Sofort-Anzeige von frisch erstellten Objekten (Terminanfrage,
 * Beleg-Upload), bis der Server-Sync sie liefert.
 *
 * Idee (vom Mandantenportal gewünscht): das neue Objekt wird beim Speichern in
 * den localStorage geschrieben und sofort angezeigt. Sobald die echten Daten
 * vom Server eintreffen (oder ein Maximalalter erreicht ist), wird der
 * optimistische Eintrag wieder entfernt. localStorage statt nur Query-Cache,
 * damit es einen Reload übersteht — der Sync kann (z. B. beim Beleg) Minuten
 * dauern.
 */

const CHANNEL = 'dino:optimistic-changed';

export interface OptimisticBase {
  /** Stabile temporäre ID des optimistischen Eintrags. */
  _optimisticId: string;
  /** Zeitstempel (ms) zum Aufräumen alter, nie eingetroffener Einträge. */
  _ts: number;
}

const read = <T,>(key: string): T[] => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T[]) : [];
  } catch {
    return [];
  }
};

const write = <T,>(key: string, items: T[]): void => {
  try {
    if (items.length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(items));
    }
  } catch {
    /* localStorage nicht verfügbar / Quota — optimistische Anzeige ist best effort */
  }
  // Gleicher Tab bekommt kein natives 'storage'-Event → eigenes Event, damit
  // ein bereits gemounteter Reader (z. B. die Anzeige-Komponente) sofort
  // neu liest, wenn an anderer Stelle (Formular) etwas hinzugefügt wurde.
  window.dispatchEvent(new Event(CHANNEL));
};

/** Hängt einen optimistischen Eintrag an und benachrichtigt aktive Reader. */
export const addOptimistic = <T extends OptimisticBase>(
  key: string,
  item: T
): void => {
  write(key, [...read<T>(key), item]);
};

/** Erzeugt eine eindeutige temporäre ID für einen optimistischen Eintrag. */
export const makeOptimisticId = (): string =>
  `opt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Liefert die noch ausstehenden optimistischen Einträge für `key`.
 * `serverItems` = die echten Daten; sobald `isSynced(pending, serverItems)`
 * für einen Eintrag true ist (oder er älter als `maxAgeMs` ist), wird er
 * entfernt.
 */
export function useOptimisticItems<T extends OptimisticBase>(
  key: string,
  serverItems: unknown[],
  isSynced: (pending: T, serverItems: unknown[]) => boolean,
  maxAgeMs: number = 15 * 60_000
): T[] {
  const [pending, setPending] = useState<T[]>(() => read<T>(key));

  // Auf Hinzufügen aus anderen Komponenten reagieren.
  useEffect(() => {
    const handler = () => setPending(read<T>(key));
    window.addEventListener(CHANNEL, handler);
    return () => window.removeEventListener(CHANNEL, handler);
  }, [key]);

  // Reconcile, sobald sich die Server-Daten ändern (Polling/Refetch): bereits
  // gesyncte bzw. zu alte Einträge entfernen.
  useEffect(() => {
    const now = Date.now();
    const current = read<T>(key);
    const still = current.filter(
      p => !isSynced(p, serverItems) && now - p._ts < maxAgeMs
    );
    if (still.length !== current.length) {
      write(key, still); // löst CHANNEL aus -> setPending via Handler
    } else {
      setPending(current);
    }
    // isSynced bewusst nicht in den Deps (wird je Render neu erzeugt); der
    // Reconcile soll bei Server-Daten-Änderung laufen.
  }, [key, serverItems]);

  return pending;
}
